import { CoreEvent } from "@nrk/player-core/types";
import { ReactiveState } from "@nrk/reactive-element";
import { getLogger } from "../utils/logging";
const _logger = getLogger("SubtitleState");
class SubtitleState extends ReactiveState {
  isShowing = false;
  active;
  activeLabel;
  activeCues = [];
  subtitles = [];
  _session;
  constructor(session, manifest, preferredSubtitle) {
    super();
    this._session = session;
    this._init(manifest, preferredSubtitle);
    session.on(CoreEvent.SubtitlesCueUpdate, this._onSubtitleCueUpdate, this);
    session.on(CoreEvent.SubtitlesChange, this._onSubtitleChange, this);
  }
  toggleVisibility() {
    if (!this._session) {
      return;
    }
    if (this.isShowing) {
      this.isShowing = false;
      this._session.setNoSubtitles();
    } else {
      this.isShowing = true;
      this._session.setAutoSubtitles();
    }
  }
  destroy() {
    super.destroy();
    this._session = void 0;
  }
  async _init(manifest, preferredSubtitle) {
    let subtitles = this._session.getAvailableSubtitles();
    if (manifest) {
      if (subtitles?.length === 0) {
        subtitles = await addExternalSubtitles(this._session, manifest);
      }
      if (!this._session) {
        return;
      }
      if (preferredSubtitle) {
        const preference = subtitles.find((s) => s.label === preferredSubtitle);
        if (preference) {
          this._session.setActiveSubtitles(preference.id, { byUser: false });
        } else if (preferredSubtitle === "none") {
          this._session.setNoSubtitles({ byUser: false });
        }
      } else {
        this._session.setDefaultSubtitles();
        if (this._session.getActiveSubtitles() === void 0) {
          const defaultSubtitleId = findDefaultSubtitleId(subtitles, manifest);
          if (defaultSubtitleId) {
            this._session.setActiveSubtitles(defaultSubtitleId, { byUser: false });
          }
        }
      }
    }
    this.isShowing = this._session.getActiveSubtitles() !== void 0;
    this._session.disableNativeSubtitlesDisplay();
    this.subtitles = subtitles;
  }
  _onSubtitleCueUpdate(event) {
    this.activeCues = event.activeCues;
  }
  _onSubtitleChange(event) {
    this.active = event.active ? event.active.id : void 0;
    this.activeLabel = event.active ? event.active.label : void 0;
    this.isShowing = event.active ? true : false;
  }
}
async function addExternalSubtitles(session, manifest) {
  const subtitles = [];
  for (const subtitle of manifest.playable.subtitles) {
    try {
      const variant = normalizeSubtitleType(subtitle.type);
      const addedSubtitle = await session.addSubtitlesTrack(
        subtitle.webVtt,
        subtitle.label,
        subtitle.language,
        variant,
        {
          defaultOn: subtitle.defaultOn
        }
      );
      if (addedSubtitle) {
        subtitles.push(addedSubtitle);
      }
    } catch (error) {
      _logger.error(error);
    }
  }
  return subtitles;
}
function normalizeSubtitleType(type) {
  const mapping = {
    nor: void 0,
    gbp: void 0,
    ttv: "public.accessibility.describes-spoken-dialog",
    mix: "public.accessibility.describes-spoken-dialog,public.accessibility.describes-music-and-sound"
  };
  return type in mapping ? mapping[type] : type;
}
function findDefaultSubtitleId(subtitles, manifest) {
  const defaultManifestSubtitle = manifest.playable.subtitles.find((sub) => sub.defaultOn);
  if (!defaultManifestSubtitle) {
    return void 0;
  }
  if (subtitles.length === 1) {
    return subtitles[0].id;
  }
  const defaultSubtitle = subtitles.find(
    (sub) => sub.label === defaultManifestSubtitle.label || sub.label === defaultManifestSubtitle.type
  );
  return defaultSubtitle?.id;
}
export {
  SubtitleState
};
